import {
    isManualSectionIIEntry,
    SECTION_II_ENTRY_PARAMS,
    SECTION_II_VALUE_FIELDS,
    sectionIIRowsFromEntryParams,
} from "@reva/shared/register";
import equal from "fast-deep-equal";

import {
    selectAllInitialValuationParams,
    selectAllRegisterParams,
    selectCommentForParam,
    selectSegment,
    selectSingleRegisterParam,
} from "./objectParametersSlice";
import {clearedComments, updateMultipleParamsAndValidateAndSubmitFormThunk} from "./objectParametersThunk";
import {selectReadOnly} from "./persistenceSlice";
import type {RevaThunkAction} from "./revaHooks";
import {selectSpecApiParamInputs} from "./specSlice";
import type {RevaRootState} from "./store";

/** Eine Zeile von register_abt2; `newRow` markiert die leere Eingabezeile der Tabelle. */
export type SectionIIRow = Record<string, unknown> & {newRow?: boolean};

const DEFAULT_CATEGORIES: Record<string, number> = Object.fromEntries(
    Object.values(SECTION_II_VALUE_FIELDS).map(({category, defaultCategory}) => [category, defaultCategory]),
);

/**
 * Ob die AVM Prozentsätze und Beleihungswerte je Abt.-II-Eintrag kennt.
 * TODO #19602: Nach dem AVM-Release auf LIVE entfernen, samt Pfaden und Tests ohne Prozentsätze.
 */
export const selectHasSectionIIPercentages = (state: RevaRootState): boolean =>
    selectSpecApiParamInputs(state)?.[selectSegment(state)]?.section_ii_entry_percentage != null;

/**
 * Leitet die AVM-Eingaben section_ii_entry_* aus den manuell erfassten Zeilen ab. Beide werden gespeichert:
 * die AVM-Eingaben haben eine eigene Ebenen- und Konfliktlogik (u.a. gegenüber der Erstbewertung),
 * register_abt2 hält die vollständigen Zeilen inkl. Text/Bemerkung. Leere Felder gehen als null an die AVM,
 * eine 0 hätte dort als angegebener Wert Vorrang vor dem Prozentsatz. Die Kategorien gehen immer mit (#19602).
 */
export function sectionIIEntryParams(rows: SectionIIRow[]): Record<string, unknown[]> {
    const manualRows = rows.filter(isManualSectionIIEntry);
    return Object.fromEntries(
        SECTION_II_ENTRY_PARAMS.map((field) => [
            field,
            manualRows.map((row) => row[field] ?? DEFAULT_CATEGORIES[field] ?? null),
        ]),
    );
}

/**
 * Ein Request für alles, damit sich die Antworten nicht überholen. Mit changedField wird nur dieses
 * section_ii_entry_*-Array mitgeschrieben, sonst alle.
 */
export const saveSectionIIRows =
    (rows: SectionIIRow[], changedField?: string): RevaThunkAction<void> =>
    (dispatch, getState) => {
        const entryParams = Object.entries(sectionIIEntryParams(rows)).filter(
            ([parameter]) => changedField == null || parameter === changedField,
        );
        dispatch(
            updateMultipleParamsAndValidateAndSubmitFormThunk(
                "REGISTER",
                {...Object.fromEntries(entryParams), register_abt2: rows},
                {
                    section_ii_entry_value:
                        selectCommentForParam(getState(), "section_ii_entry_value", "REGISTER") ?? null,
                    section_ii_entry_type_de: null,
                    section_ii_entry_subtype_de: null,
                },
            ),
        );
    };

/** Abt.-II-Zeilen der Erstbewertung, deren Unterart noch fehlt; jede Unterart kommt nur einmal vor. */
export function missingSectionIIRows(
    initialValuationParams: object | null | undefined,
    existingRows: SectionIIRow[] = [],
): SectionIIRow[] {
    return sectionIIRowsFromEntryParams(initialValuationParams).filter((imported) =>
        existingRows.every((row) => row.section_ii_entry_subtype_de !== imported.section_ii_entry_subtype_de),
    );
}

const sectionIIRowsFromInitialValuation = (state: RevaRootState, existingRows: SectionIIRow[] = []) =>
    missingSectionIIRows(selectAllInitialValuationParams(state), existingRows);

/**
 * register_abt2 bleibt wie gespeichert: withLiveLeaseholdEntries würde beim Laden ohne aktuelle Indikation
 * die gespeicherte Erbbau-/Vorkaufsrecht-Zeile samt Text verwerfen.
 */
export const importSectionIIFromInitialValuation = (): RevaThunkAction<void> => (dispatch, getState) => {
    const state = getState();
    if (selectReadOnly(state) || selectSingleRegisterParam(state, "section_ii_entry_value") != null) return;

    const rows: SectionIIRow[] = selectSingleRegisterParam(state, "register_abt2") ?? [];
    const importedRows = sectionIIRowsFromInitialValuation(state, rows);
    if (importedRows.length) dispatch(saveSectionIIRows([...rows, ...importedRows]));
};

const LEASEHOLD_PARAMS = [
    "prop_leasehold_amount_pa",
    "prop_leasehold_duration",
    "prop_leasehold_interest_rate",
    "prop_leasehold_interest_rate_ground",
    "prop_leasehold_general_disadvantage",
    "prop_leasehold_building_compensation",
    "prop_leasehold",
];

export function leaseholdParams(value: unknown): Record<string, unknown> {
    return Object.fromEntries(LEASEHOLD_PARAMS.map((parameter) => [parameter, value]));
}

/** Daraus berechnet die AVM die Erbbau- und die Vorkaufsrecht-Zeile von Abt. II. */
const LEASEHOLD_AND_PREEMPTION_PARAMS = [...LEASEHOLD_PARAMS, "prop_preemption_right"];

const leaseholdAndPreemptionParams = (value: unknown) =>
    Object.fromEntries(LEASEHOLD_AND_PREEMPTION_PARAMS.map((parameter) => [parameter, value]));

/** Derselbe Stand, den sonst der Import beim nächsten Laden herstellen würde. */
function sectionIIResetParams(state: RevaRootState): Record<string, unknown> {
    const initialRows = sectionIIRowsFromInitialValuation(state);
    return {
        ...Object.fromEntries(SECTION_II_ENTRY_PARAMS.map((parameter) => [parameter, null])),
        ...leaseholdAndPreemptionParams(null),
        register_abt2: null,
        ...(initialRows.length ? {...sectionIIEntryParams(initialRows), register_abt2: initialRows} : {}),
    };
}

/** Gleicht ab, was beim Speichern ohnehin verloren geht: undefined/leere Felder, undefined in Arrays. */
function normalized(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(normalized);
    if (value && typeof value === "object") {
        return Object.fromEntries(
            Object.entries(value)
                .filter(([, v]) => v != null && v !== "")
                .map(([key, v]) => [key, normalized(v)]),
        );
    }
    return value ?? null;
}

/**
 * Nicht gesetzte Werte und Erbbau-/Vorkaufsrecht-Angaben wie in der Erstbewertung zählen als unverändert, dort
 * greift ohnehin die Erstbewertung. Die Erbbau-/Vorkaufsrecht-Zeile in register_abt2 folgt aus der Indikation.
 */
export function selectCanResetSectionII(state: RevaRootState): boolean {
    const current = (selectAllRegisterParams(state) ?? {}) as Record<string, unknown>;
    const initial = (selectAllInitialValuationParams(state) ?? {}) as Record<string, unknown>;
    const manualRows = (rows: unknown) =>
        Array.isArray(rows) ? normalized(rows.filter(isManualSectionIIEntry)) : normalized(rows);

    return Object.entries(sectionIIResetParams(state)).some(([parameter, target]) => {
        if (current[parameter] == null) return false;
        if (LEASEHOLD_AND_PREEMPTION_PARAMS.includes(parameter)) return !equal(current[parameter], initial[parameter]);
        if (parameter === "register_abt2") return !equal(manualRows(current[parameter]), manualRows(target));
        return !equal(normalized(current[parameter]), normalized(target));
    });
}

/** `null` setzt auf die Erstbewertung zurück, `[]` leert Abt. II. */
export const resetOrEmptySectionII =
    (value: unknown): RevaThunkAction<void> =>
    (dispatch, getState) => {
        const params =
            value == null
                ? sectionIIResetParams(getState())
                : {
                      ...Object.fromEntries(SECTION_II_ENTRY_PARAMS.map((parameter) => [parameter, []])),
                      ...leaseholdAndPreemptionParams("empty"),
                      register_abt2: [],
                  };
        dispatch(updateMultipleParamsAndValidateAndSubmitFormThunk("REGISTER", params, clearedComments(params)));
    };
